import { describeError, useApi } from '@classgo/app-kit';
import { Badge, Button, Card, EmptyState, Input, useToast } from '@classgo/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';

import { billingApi, billingQueries } from '../../billing/api';
import { useT } from '../../i18n';
import { can } from '../../shell';
import { useTenant } from '../../tenant/tenant-provider';

/**
 * The reminders tab (ticket #117): the days before and after the due date on which the daily job
 * reminds the families (`GET`/`PUT /billing/reminder-settings`), and whether the school still
 * runs on the defaults.
 *
 * @returns The tab.
 */
export const RemindersTab = () => {
  const t = useT();
  const api = useApi();
  const toast = useToast();
  const queries = useQueryClient();
  const { session } = useTenant();
  const settings = useQuery(billingQueries.reminderSettings(api));
  const [before, setBefore] = useState('');
  const [after, setAfter] = useState('');
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    if (settings.data) {
      setBefore(String(settings.data.reminderDaysBefore));
      setAfter(String(settings.data.overdueDaysAfter));
    }
  }, [settings.data]);
  const save = useMutation({
    mutationFn: () =>
      billingApi(api).putReminderSettings({
        reminderDaysBefore: Number(before),
        overdueDaysAfter: Number(after),
      }),
    onSuccess: (saved) => {
      setError(null);
      queries.setQueryData(billingQueries.reminderSettings(api).queryKey, saved);
      toast.show({ tone: 'success', title: t('billing.reminders.saved') });
    },
    onError: (thrown) => setError(describeError(t, thrown)),
  });
  if (settings.isError)
    return (
      <Card title={t('billing.reminders.title')}>
        <EmptyState
          title={t('entity.error.title')}
          description={describeError(t, settings.error)}
        />
      </Card>
    );
  return (
    <Card title={t('billing.reminders.title')}>
      <p className="mb-3 text-sm text-gray-600">{t('billing.reminders.intro')}</p>
      {settings.data ? (
        <p className="mb-3">
          <Badge tone={settings.data.isDefault ? 'gray' : 'brand'}>
            {settings.data.isDefault
              ? t('billing.reminders.default')
              : t('billing.reminders.custom')}
          </Badge>
        </p>
      ) : null}
      <form
        className="space-y-3"
        onSubmit={(event) => {
          event.preventDefault();
          save.mutate();
        }}
      >
        {error ? (
          <p role="alert" className="text-sm text-danger-700">
            {error}
          </p>
        ) : null}
        <div className="grid gap-3 sm:grid-cols-2">
          <Input
            label={t('billing.reminders.before')}
            help={t('billing.reminders.before.help')}
            type="number"
            min={0}
            max={30}
            step={1}
            required
            value={before}
            disabled={settings.isPending}
            onChange={(event) => setBefore(event.target.value)}
          />
          <Input
            label={t('billing.reminders.after')}
            help={t('billing.reminders.after.help')}
            type="number"
            min={1}
            max={30}
            step={1}
            required
            value={after}
            disabled={settings.isPending}
            onChange={(event) => setAfter(event.target.value)}
          />
        </div>
        {can(session, 'billing:configure') ? (
          <div className="flex justify-end">
            <Button type="submit" loading={save.isPending} disabled={settings.isPending}>
              {t('common.save')}
            </Button>
          </div>
        ) : null}
      </form>
    </Card>
  );
};
